import { Component, OnInit, signal, inject } from '@angular/core';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { InputWithErrorComponent } from '@nimmsta/ng-commons-lib/forms';
import { Organization, User, UserRole } from '@nimmsta/ng-commons-lib/models';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { OrganizationUserService } from '../../services/organization-user.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { PrimeTemplate } from 'primeng/api';
import { LaddaModule } from 'angular2-ladda';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { combineLatest, map, switchMap } from 'rxjs';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    Select,
    PrimeTemplate,
    LaddaModule,
    TranslatePipe,
    HeaderBarComponent,
    InputWithErrorComponent,
    ErrorComponent,
  ],
})
export class OrganizationSettingsComponent implements OnInit {
  private authService = inject(AuthService);
  private orgService = inject(OrganizationService);
  private userService = inject(OrganizationUserService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  organization!: Organization;

  form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    owner: new FormControl<User | null>(null),
  });

  error?: HttpErrorResponse;

  loading = false;

  changed = signal<boolean>(false);

  users: User[] = [];

  ngOnInit() {
    combineLatest([this.authService.getCurrentUser(), this.route.paramMap])
      .pipe(
        switchMap(([user, params]) => {
          this.loading = true;
          return this.orgService
            .get(params.get('id')!)
            .pipe(map((organization) => ({ user, organization })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ user, organization }) => {
          this.organization = organization;
          this.form.patchValue(
            { name: organization.name, owner: organization.owner ?? null },
            { emitEvent: false },
          );
          this.changed.set(false);
          this.loading = false;
          this.userService.setOrgId(this.organization.id);
          if (
            user.role !== UserRole.ADMIN &&
            this.organization.owner &&
            this.organization.owner.id !== user.id
          ) {
            this.form.controls.owner.disable({ emitEvent: false });
          } else {
            this.form.controls.owner.enable({ emitEvent: false });
          }
          this.userService
            .list({
              filter: null,
              sortColumn: 'family_name',
              pageSize: 9999,
            })
            .subscribe((users) => {
              this.users = users.content;
            });
        },
        error: (error) => {
          this.error = error;
          this.loading = false;
        },
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.organization) return;
        if (
          this.form.value.name !== this.organization.name ||
          this.form.value.owner?.id !== this.organization.owner?.id
        ) {
          this.changed.set(true);
        } else {
          this.changed.set(false);
        }
      });
  }

  save() {
    if (this.loading || !this.organization || !this.form.valid) return;
    this.loading = true;
    this.error = undefined;
    const orgUpdate = {
      ...this.organization,
      name: this.form.get('name')!.value!,
      ownerId: this.form.get('owner')!.value?.id,
    };
    this.orgService.update(orgUpdate).subscribe({
      next: () => {
        this.authService.loadCurrentUser().subscribe({
          error: (error: HttpErrorResponse) => {
            this.error = error;
            this.loading = false;
          },
        });
      },
      error: (error) => {
        this.error = error;
        this.loading = false;
      },
    });
  }
}
